import { Platform, useWindowDimensions } from "react-native"

import { useSafeAreaInsets } from "react-native-safe-area-context"

/**
 * Default status bar height for iOS when we need to simulate it.
 * This is the standard status bar height on most iOS devices.
 */
const IOS_STATUS_BAR_HEIGHT = 20

/**
 * Minimum threshold for considering the status bar as "visible".
 * If insets.top is below this value in landscape, the status bar is likely hidden.
 */
const STATUS_BAR_VISIBILITY_THRESHOLD = 5

/**
 * Hook to detect if the status bar is visible and provide compensation padding.
 *
 * On iOS in landscape mode, the status bar is often hidden by the system, but
 * the safe area insets may not properly reflect this (reporting 0 for top inset).
 * This hook detects this situation and provides a compensation value that can be
 * used to add extra padding to maintain consistent layout.
 *
 * @returns Object with:
 *   - isStatusBarVisible: boolean indicating if status bar appears to be visible
 *   - compensationPadding: number of pixels to add as extra top padding when status bar is hidden
 *   - effectiveTopInset: the recommended top inset to use (insets.top + compensationPadding)
 */
export function useStatusBarCompensation() {
  const insets = useSafeAreaInsets()
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  // On iOS in landscape mode, if the top inset is very small (near 0),
  // the status bar is likely hidden and we should add compensation padding
  const isIOS = Platform.OS === "ios"

  // Status bar is considered "visible" if:
  // 1. We're not on iOS (Android handles this differently), OR
  // 2. We're in portrait mode (status bar typically visible), OR
  // 3. The top inset is significant (> threshold)
  const isStatusBarVisible = !isIOS || !isLandscape || insets.top > STATUS_BAR_VISIBILITY_THRESHOLD

  // Compensation padding is applied when:
  // - We're on iOS
  // - We're in landscape mode
  // - The top inset suggests the status bar is hidden
  const compensationPadding =
    isIOS && isLandscape && insets.top <= STATUS_BAR_VISIBILITY_THRESHOLD
      ? IOS_STATUS_BAR_HEIGHT
      : 0

  // The effective top inset combines the actual inset with any compensation
  const effectiveTopInset = insets.top + compensationPadding

  return {
    isStatusBarVisible,
    compensationPadding,
    effectiveTopInset,
    // Also expose the raw values for debugging
    rawTopInset: insets.top,
    isLandscape,
  }
}
